import {EventEmitter, Injectable} from '@angular/core';
import {isEmbeddedInIframe, salesChannel} from '../../core/utils/utils';
import {OverlayContainer} from '@angular/cdk/overlay';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import ResizeObserver from 'resize-observer-polyfill';
import {Subscription} from 'rxjs/Rx';
import {take} from 'rxjs/operators';
import {ScrollHelper} from './scroll-helper';
import {SalesChannel} from '../../core/consts/sales-channel.enum';

@Injectable({
  providedIn: 'root'
})
export class NativeIframeCommunicationService {

  private positionData: IframePositionData;
  private modalOpened: boolean = false;
  private modalHeightChange: EventEmitter<number> = new EventEmitter<number>();
  private modalHeightChangeSubscription: Subscription;
  private behaviourStrategy: IframeModalBehaviourStrategy;
  private resizedModalHeight: number;
  private salesChannelConfig: SalesChannelIframeEventsConfig;

  private config: NativeIframeConfig = {
    modalAlignment: IframeModalAlignment.CENTER
  };

  private readonly SALES_CHANNELS_CONFIG: SalesChannelIframeEventsConfig[] = [
    {
      salesChannel: SalesChannel.NESTBANK,
      disabledEvents: [],
      paymentRedirectBehaviour: IframePaymentRedirectBehaviour.DISABLED
    },
    {
      salesChannel: SalesChannel.BANKIER,
      disabledEvents: [],
      paymentRedirectBehaviour: IframePaymentRedirectBehaviour.PARENT_REDIRECT
    }
  ]

  get communicationEnabled(): boolean {
    return isEmbeddedInIframe();
  }

  get overlayContainerElement(): HTMLElement {
    return this.overlayContainer.getContainerElement();
  }

  get overlayPaneElement(): HTMLElement {
    return <HTMLElement>this.overlayContainerElement.querySelector('.cdk-overlay-pane');
  }

  get matDialogContainer(): HTMLElement {
    return <HTMLElement>this.overlayContainerElement.querySelector('.cdk-overlay-pane .mat-dialog-container');
  }

  get iframeBoundingClientRect(): DOMRect {
    return this.positionData.iframeBoundingClientRect;
  }

  get modalHeight(): number | null {
    return this.overlayPaneElement ? this.overlayPaneElement.offsetHeight : null;
  }

  get paymentRedirectBehaviour(): IframePaymentRedirectBehaviour {
    return this.salesChannelConfig ? this.salesChannelConfig.paymentRedirectBehaviour : null;
  }

  constructor(private overlayContainer: OverlayContainer) {

    if (this.communicationEnabled) {
      this.initSalesChannelConfig();
      this.enableResizeObserver();
      this.handlePositionData();
      this.handleScrollingInsideIframe();
    }
  }

  onModalOpen(): void {
    if (this.communicationEnabled) {
      this.modalOpened = true;
      this.sendModalOpenedEvent();
    }
  }

  onModalClose(): void {
    if (this.communicationEnabled) {
      if (isNotNullOrUndefined(this.modalHeightChangeSubscription)) {
        this.modalHeightChangeSubscription.unsubscribe();
        this.modalHeightChangeSubscription = null;
        this.resizedModalHeight = null;
      }
      this.modalOpened = false;
      this.sendModalClosedEvent();
    }
  }

  enableResizeObserver() {
    const resizeObserver = new ResizeObserver(entries => {
      const height = entries[0].target.clientHeight;
      this.sendResizeData({
        height: height
      });
    });
    resizeObserver.observe(document.body);
  }

  attachModalResizedObserver() {
    if (this.matDialogContainer) {
      const resizeObserver = new ResizeObserver(entries => {
        const height = entries[0].target.clientHeight;
        if (height !== this.resizedModalHeight) {
          this.resizedModalHeight = height;
          this.modalHeightChange.emit(height);
        }
      });
      resizeObserver.observe(this.matDialogContainer);
    }
  }

  onStepCreated(stepWithAccumulatedSections: boolean): void {
    this.postMessage({
      eventName: NativeIframeEventName.STEP_CREATED,
      payload: {
        stepWithAccumulatedSections: stepWithAccumulatedSections
      }
    });
  }

  sendModalOpenedEvent(): void {
    this.postMessage({
      eventName: NativeIframeEventName.MODAL_OPENED
    });
  }

  sendModalClosedEvent(): void {
    this.postMessage({
      eventName: NativeIframeEventName.MODAL_CLOSED
    });
  }

  sendOfferPayEvent(paymentUri: string): void {
    this.postMessage({
      eventName: NativeIframeEventName.OFFER_PAY,
      payload: {
        paymentUri: paymentUri
      }
    });
  }

  sendScrollIntoEvent(y: number): void {
    this.postMessage({
      eventName: NativeIframeEventName.SCROLL_INTO,
      payload: {
        scrollY: y
      }
    });
  }

  private handlePositionData(): void {
    window.addEventListener('message', event => {
      const iframeEvent: NativeIframeEvent = event ? event.data : null;
      if (isNotNullOrUndefined(iframeEvent) && isNotNullOrUndefined(iframeEvent.eventName)) {
        if (iframeEvent.eventName === NativeIframeEventName.POSITION_DATA) {
          this.positionData = iframeEvent.payload ? iframeEvent.payload.positionData : null;
          if (isNotNullOrUndefined(this.positionData.behaviourStrategy)) {
            this.behaviourStrategy = this.positionData.behaviourStrategy;
          }
          if (this.modalOpened) {
            setTimeout(() => this.updateModalPosition(), 10);
          }
        }
      }
    });
  }

  private handleScrollingInsideIframe(): void {
    ScrollHelper.scrollToElement$.subscribe(event => {
      const { selector, offset } = event;
      const element = document.querySelector(selector);
      if (isNotNullOrUndefined(element) && isNotNullOrUndefined(this.positionData)) {
        const elementBoundingClientRect = element.getBoundingClientRect();
        const areaAboveIframeHeight = this.positionData.pageYOffset + this.positionData.iframeBoundingClientRect.top;
        let scrollY = elementBoundingClientRect.top + areaAboveIframeHeight;
        if (isNotNullOrUndefined(offset)) {
          scrollY -= offset;
        }
        this.sendScrollIntoEvent(scrollY);
      }
    });
  }

  private updateModalPosition(): void {
    if (this.overlayContainerElement) {
      const iframeViewportPosition = this.getIframeViewportPosition();
      const visibleIframeHeight = this.calculateVisibleIframeHeight(iframeViewportPosition);
      this.applyOffsetToOverlayPane(iframeViewportPosition, visibleIframeHeight);
      if (!isNotNullOrUndefined(this.modalHeightChangeSubscription)) {
        this.attachModalResizedObserver();
        this.modalHeightChangeSubscription = this.modalHeightChange
            .pipe(
                take(3) // handle modal's height change caused by loading images
            )
            .subscribe(() => {
              setTimeout(() => this.applyOffsetToOverlayPane(iframeViewportPosition, visibleIframeHeight), 0);
            });
      }
    }
  }

  private applyOffsetToOverlayPane(position: IframeViewportPosition, visibleIframeHeight: number) {
    if (isNotNullOrUndefined(this.overlayPaneElement)) {
      const {top, bottom} = this.getIframeModalOffsetData(position, visibleIframeHeight);
      this.overlayPaneElement.style.top = isNotNullOrUndefined(top) ? `${top}px` : 'unset';
      this.overlayPaneElement.style.bottom = isNotNullOrUndefined(bottom) ? `${bottom}px` : 'unset';
      if (this.behaviourStrategy !== IframeModalBehaviourStrategy.STANDARD) {
        this.overlayPaneElement.style.maxHeight = `${visibleIframeHeight}px`;
      }
    }
  }

  private postMessage(message: NativeIframeEvent) {
    if (this.communicationEnabled && isNotNullOrUndefined(window.parent) && this.isEventEnabled(message.eventName)) {
      window.parent.postMessage(message, '*');
    }
  }

  private sendResizeData(data: IframeResizeData): void {
    this.postMessage({
      eventName: NativeIframeEventName.RESIZE,
      payload: {
        resizeData: data
      }
    });
  }

  private getIframeViewportPosition(): IframeViewportPosition {
    const pageTopIframeBottom = this.positionData.pageYOffset + this.iframeBoundingClientRect.bottom;
    const pageTopViewportBottom = this.positionData.pageYOffset + this.positionData.viewportHeight;

    if (this.iframeBoundingClientRect.top > 0) {
      return pageTopIframeBottom > pageTopViewportBottom ? IframeViewportPosition.IFRAME_TOP_VISIBLE : IframeViewportPosition.IFRAME_INSIDE_VIEWPORT;
    } else {
      return pageTopIframeBottom > pageTopViewportBottom ? IframeViewportPosition.VIEWPORT_INSIDE_IFRAME : IframeViewportPosition.IFRAME_BOTTOM_VISIBLE;
    }
  }

  private calculateVisibleIframeHeight(position: IframeViewportPosition): number {
    switch (position) {
      case IframeViewportPosition.VIEWPORT_INSIDE_IFRAME:
        return this.positionData.viewportHeight;
      case IframeViewportPosition.IFRAME_INSIDE_VIEWPORT:
        return this.iframeBoundingClientRect.bottom - this.iframeBoundingClientRect.top;
      case IframeViewportPosition.IFRAME_TOP_VISIBLE:
        return this.positionData.viewportHeight - this.iframeBoundingClientRect.top;
      case IframeViewportPosition.IFRAME_BOTTOM_VISIBLE:
        return this.iframeBoundingClientRect.bottom;
    }
  }

  private getIframeModalOffsetData(position: IframeViewportPosition, visibleIframeHeight: number): IframeModalOffsetData {
    if (this.modalHeight >= visibleIframeHeight) { // no space to display modal aligned top or center - attach to iframe's bottom edge
      if (position === IframeViewportPosition.IFRAME_BOTTOM_VISIBLE) {
        return {
          bottom: 0
        };
      } else {
        return this.calculateModalOffsetForTopAlignment(position);
      }
    }

    switch (this.config.modalAlignment) {
      case IframeModalAlignment.TOP:
        return this.calculateModalOffsetForTopAlignment(position);
      case IframeModalAlignment.CENTER:
        return this.calculateModalOffsetForCenterAlignment(position, visibleIframeHeight);
      case IframeModalAlignment.BOTTOM:
        return this.calculateModalOffsetForBottomAlignment(position);
    }
  }

  private calculateModalOffsetForTopAlignment(position: IframeViewportPosition): IframeModalOffsetData {
    if (position === IframeViewportPosition.IFRAME_TOP_VISIBLE || position === IframeViewportPosition.IFRAME_INSIDE_VIEWPORT) {
      return {
        top: 0
      };
    } else {
      return {
        top: -this.iframeBoundingClientRect.top
      };
    }
  }

  private calculateModalOffsetForCenterAlignment(position: IframeViewportPosition, visibleIframeHeight: number): IframeModalOffsetData {
    if (position === IframeViewportPosition.IFRAME_INSIDE_VIEWPORT || position === IframeViewportPosition.IFRAME_TOP_VISIBLE) {
      return {
        top: (visibleIframeHeight - this.modalHeight) / 2
      };
    } else {
      return {
        top: -this.iframeBoundingClientRect.top + (visibleIframeHeight - this.modalHeight) / 2
      };
    }
  }

  private calculateModalOffsetForBottomAlignment(position: IframeViewportPosition): IframeModalOffsetData {
    if (position === IframeViewportPosition.IFRAME_BOTTOM_VISIBLE || position === IframeViewportPosition.IFRAME_INSIDE_VIEWPORT) {
      return {
        bottom: 0
      };
    } else {
      const hiddenBottomIframeAreaHeight = this.iframeBoundingClientRect.bottom - this.positionData.viewportHeight;
      return {
        bottom: hiddenBottomIframeAreaHeight
      };
    }
  }

  private initSalesChannelConfig(): void {
    this.salesChannelConfig = this.SALES_CHANNELS_CONFIG.find(item => item.salesChannel === salesChannel());
  }

  private isEventEnabled(eventName: NativeIframeEventName): boolean {
    const disabledEvents = this.salesChannelConfig ? this.salesChannelConfig.disabledEvents : [];
    return !disabledEvents.includes(eventName);
  }
}

export enum NativeIframeEventName {
  STEP_CREATED = 'STEP_CREATED', // iframe -> parent
  RESIZE = 'RESIZE', // iframe -> parent
  MODAL_OPENED = 'MODAL_OPENED', // iframe -> parent
  MODAL_CLOSED = 'MODAL_CLOSED', // iframe -> parent
  POSITION_DATA = 'POSITION_DATA', // parent -> iframe
  OFFER_PAY = 'OFFER_PAY', // iframe -> parent
  SCROLL_INTO = 'SCROLL_INTO', // iframe -> parent
}

export interface NativeIframeEventPayload {
  positionData?: IframePositionData;
  resizeData?: IframeResizeData;
  stepWithAccumulatedSections?: boolean;
  scrollY?: number;
  paymentUri?: string;
}

export interface NativeIframeEvent {
  eventName: NativeIframeEventName;
  payload?: NativeIframeEventPayload;
}

export interface NativeIframeConfig {
  modalAlignment: IframeModalAlignment;
}

export interface IframeResizeData {
  height: number;
}

export interface IframeModalOffsetData {
  top?: number;
  bottom?: number;
}

export interface IframePositionData {
  iframeBoundingClientRect: DOMRect; // iframe.getBoundingClientRect()
  pageYOffset: number; // invisible top (scrolled) content height (window.pageYOffset)
  pageHeight: number; // document.documentElement.scrollHeight
  viewportHeight: number; // window.innerHeight
  behaviourStrategy?: IframeModalBehaviourStrategy;
}

export enum IframeModalAlignment {
  TOP = 'TOP', // modal's top edge aligned to iframe's visible area top edge
  CENTER = 'CENTER', // modal is centered vertically in iframe's visible area
  BOTTOM = 'BOTTOM', // modal's bottom edge aligned to iframe's visible area bottom edge
}

export interface SalesChannelIframeEventsConfig {
  salesChannel: SalesChannel;
  disabledEvents: NativeIframeEventName[];
  paymentRedirectBehaviour: IframePaymentRedirectBehaviour;
}

export enum IframeViewportPosition {
  /**
   * iframe's top edge is higher than viewport's top edge,
   * iframe's bottom edge is lower than viewport's bottom edge,
   */
  VIEWPORT_INSIDE_IFRAME = 'VIEWPORT_INSIDE_IFRAME',

  /**
   * iframe's top edge is lower than viewport's top edge,
   * iframe's bottom edge is lower than viewport's bottom edge,
   */
  IFRAME_TOP_VISIBLE = 'IFRAME_TOP_VISIBLE',

  /**
   * iframe's top edge is higher than viewport's top edge,
   * iframe's bottom edge is higher than viewport's bottom edge,
   */
  IFRAME_BOTTOM_VISIBLE = 'IFRAME_BOTTOM_VISIBLE',

  /**
   * iframe's top edge is lower than viewport's top edge,
   * iframe's bottom edge is higher than viewport's bottom edge,
   */
  IFRAME_INSIDE_VIEWPORT = 'IFRAME_INSIDE_VIEWPORT',
}

export enum IframePaymentRedirectBehaviour {
  IFRAME_REDIRECT = 'IFRAME_REDIRECT', // redirect inside iframe
  PARENT_REDIRECT = 'PARENT_REDIRECT', // redirect parent page
  DISABLED = 'DISABLED', // no redirect
}

export enum IframeModalBehaviourStrategy {
  /**
   * scrolling in parent page will be disabled when modal in iframe has been opened
   * no real time modal position updates are required
   */
  DISABLE_SCROLLING = 'DISABLE_SCROLLING',

  /**
   * scrolling isn't disabled when modal has been opened
   * modal's position is calculated once when modal is opened
   * no real time modal position updates when parent page is being scrolled
   */
  STANDARD = 'STANDARD',

  /**
   * scrolling isn't disabled when modal has been opened
   * modal's position is calculated when modal is opened and everytime parent page is being scrolled
   */
  REAL_TIME = 'REAL_TIME',
}
